/**
 * FORMAT RESOLVER
 *
 * Resolves which delivery formats are available for a given tour flow based on
 * the YAML configuration. Supports interactive tours, embedded videos and
 * quick guides (markdown checklists).
 */

import type {
  TourFlow,
  TourStep,
  TourModuleConfig,
} from '@/config/guided-tours/types';
import {
  defaultVideoManifest,
  getVideoManifestEntry,
  defaultQuickGuides,
  getQuickGuideOverride,
} from '../content';

export type TourFormat = 'interactive' | 'video' | 'quick-guide';

export interface TourFormatInfo {
  id: TourFormat;
  label: string;
  icon: string;
  description: string;
}

export const TOUR_FORMATS: TourFormatInfo[] = [
  {
    id: 'interactive',
    label: 'Interactive Tour',
    icon: 'footprints',
    description: 'Step through the flow with the mascot guide.',
  },
  {
    id: 'video',
    label: 'Video',
    icon: 'play-circle',
    description: 'Watch a short video walkthrough.',
  },
  {
    id: 'quick-guide',
    label: 'Quick Guide',
    icon: 'list-checks',
    description: 'Read a checklist summary of the flow.',
  },
];

export interface FormatResolverContent {
  videoManifest?: Record<string, { videoUrl: string; durationSeconds?: number; thumbnailUrl?: string }>;
  quickGuides?: Record<string, string>;
}

export interface FlowFormatResolution {
  flowId: string;
  flowLabel: string;
  availableFormats: TourFormat[];
  defaultFormat: TourFormat;
  videoUrl: string | null;
  quickGuideMarkdown: string | null;
  estimatedMinutes: number;
}

const DEFAULT_FORMAT_ORDER: TourFormat[] = ['interactive', 'video', 'quick-guide'];

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function deriveQuickGuideMarkdown(
  flow: TourFlow,
  content?: FormatResolverContent | null
): string {
  const override = content?.quickGuides
    ? getQuickGuideOverride(content.quickGuides, flow.id)
    : getQuickGuideOverride(defaultQuickGuides, flow.id);

  if (override) {
    return override;
  }

  const lines: string[] = [`# ${flow.label}`, '', flow.summary, ''];

  for (const step of flow.steps) {
    lines.push(`- [ ] **${step.title}**${step.summary ? ` — ${step.summary}` : ''}`);
    if (step.bullets?.length) {
      for (const bullet of step.bullets) {
        lines.push(`  - ${bullet}`);
      }
    }
  }

  return lines.join('\n');
}

function extractVideoUrl(
  flow: TourFlow,
  content?: FormatResolverContent | null
): string | null {
  // Video metadata can be attached to a flow through a dedicated `video_url`
  // field, or in practice items on the first step. This resolver prefers an
  // explicit flow-level URL, then a content manifest entry.
  const candidate = (flow as TourFlow & { video_url?: string | null }).video_url;
  if (hasText(candidate)) {
    return candidate.trim();
  }

  const manifest = content?.videoManifest ?? defaultVideoManifest;
  const entry = getVideoManifestEntry(manifest, flow.id);
  return entry?.videoUrl || null;
}

/**
 * Resolve available formats for a single flow.
 */
export function resolveFlowFormats(
  flow: TourFlow,
  preferredFormat?: TourFormat | null,
  content?: FormatResolverContent | null
): FlowFormatResolution {
  // When a rendered walkthrough video exists it becomes the suggested
  // (primary) format — available[0] drives the highlighted button in the
  // index and the defaultFormat fallback — with the interactive tour still
  // one click away.
  const available: TourFormat[] = [];

  const videoUrl = extractVideoUrl(flow, content);
  if (videoUrl) {
    available.push('video');
  }

  available.push('interactive');

  if (flow.steps.length > 0) {
    available.push('quick-guide');
  }

  const defaultFormat =
    preferredFormat && available.includes(preferredFormat)
      ? preferredFormat
      : available[0];

  return {
    flowId: flow.id,
    flowLabel: flow.label,
    availableFormats: available,
    defaultFormat,
    videoUrl,
    quickGuideMarkdown: deriveQuickGuideMarkdown(flow, content),
    estimatedMinutes: Math.max(1, Math.ceil(flow.steps.length * 0.75)),
  };
}

/**
 * Resolve formats for every flow in a module.
 */
export function resolveModuleFormats(
  moduleConfig: TourModuleConfig | null,
  preferredFormat?: TourFormat | null,
  content?: FormatResolverContent | null
): FlowFormatResolution[] {
  if (!moduleConfig) {
    return [];
  }

  return moduleConfig.flows.map((flow) => resolveFlowFormats(flow, preferredFormat, content));
}

/**
 * Resolve a single format for a step when a flow explicitly supports the
 * requested format. Used by the runner to decide which renderer to mount.
 */
export function resolveStepFormat(
  flow: TourFlow,
  step: TourStep,
  requestedFormat: TourFormat,
  content?: FormatResolverContent | null
): TourFormat {
  const resolution = resolveFlowFormats(flow, null, content);
  if (resolution.availableFormats.includes(requestedFormat)) {
    return requestedFormat;
  }
  return resolution.defaultFormat;
}

/**
 * Pick the best format when a user has a persisted preference but the
 * currently selected flow may not support it.
 */
export function pickBestFormat(
  flow: TourFlow,
  preferredFormat?: TourFormat | null,
  content?: FormatResolverContent | null
): TourFormat {
  const resolution = resolveFlowFormats(flow, preferredFormat, content);
  return resolution.defaultFormat;
}
